import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { FileCheck2, Loader2, RefreshCw, Download, X, ShieldCheck, ShieldAlert, Filter, BarChart3 } from "lucide-react";

const formatINR = (n) => `₹${Math.round(Number(n) || 0).toLocaleString("en-IN")}`;

const STATUS_META = {
  submitted:    { label: "Submitted",     tone: "text-[#D8B23E] border-[#D8B23E]/40" },
  under_review: { label: "Under review",  tone: "text-[#2FE4C6] border-[#2FE4C6]/40" },
  approved:     { label: "Approved",      tone: "text-[#3DBB84] border-[#3DBB84]/40" },
  rejected:     { label: "Rejected",      tone: "text-[#E5484D] border-[#E5484D]/40" },
  paid:         { label: "Paid",          tone: "text-[#F5F5F0] border-[#F5F5F0]/40" },
};

/**
 * Admin · Vinci Commission Desk.
 *
 * Central review queue for every order-locked commission claim.  Filters
 * by status, inspects claim details, previews all three uploaded documents,
 * and moves the claim between submitted → under_review → approved / rejected
 * / paid.  Rejection requires a documented reason (server-enforced).
 */
export function AdminCommissionDesk({ api }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("submitted");
  const [active, setActive] = useState(null);   // claim being reviewed
  const [view, setView] = useState("queue");    // "queue" | "analytics"

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/claims");
      setRows(r.data || []);
    } catch { toast.error("Could not load claims"); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    let list = rows.filter(r => r.order_id);  // only order-locked strict claims
    if (filter !== "all") list = list.filter(r => r.status === filter);
    return list;
  }, [rows, filter]);

  const counts = useMemo(() => {
    const c = { all: 0, submitted: 0, under_review: 0, approved: 0, rejected: 0, paid: 0 };
    rows.filter(r => r.order_id).forEach(r => { c.all++; c[r.status] = (c[r.status] || 0) + 1; });
    return c;
  }, [rows]);

  return (
    <div data-testid="admin-commission-desk" className="space-y-6">
      <div className="flex items-start gap-4 border-b border-white/10 pb-6">
        <div className="h-12 w-12 rounded-sm bg-[#D8B23E]/15 text-[#D8B23E] flex items-center justify-center shrink-0">
          <FileCheck2 size={20}/>
        </div>
        <div className="flex-1">
          <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#D8B23E]">Commission Desk</div>
          <h2 className="font-serif text-2xl text-[#F5F5F0] mt-1">Order-locked · one claim per trade.</h2>
          <p className="font-body text-xs text-[#8A8A82] mt-1">
            Every claim below is bound to a single Order ID. Verify the Invoice + Lorry Receipt + Unloading Photo before approval.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setView(v => v === "queue" ? "analytics" : "queue")}
                  data-testid="commission-view-toggle"
                  className={`inline-flex items-center gap-1.5 rounded-sm border px-3 py-1.5 font-grotesk text-[10px] uppercase tracking-[0.22em] transition-colors ${
                    view === "analytics" ? "bg-[#D8B23E] text-black border-[#D8B23E]" : "border-white/15 text-[#8A8A82] hover:text-[#F5F5F0]"
                  }`}>
            <BarChart3 size={11}/> {view === "queue" ? "Analytics" : "Queue"}
          </button>
          <button onClick={load} disabled={loading} className="text-[#8A8A82] hover:text-[#F5F5F0] p-1" data-testid="claims-refresh">
            {loading ? <Loader2 size={14} className="animate-spin"/> : <RefreshCw size={14}/>}
          </button>
        </div>
      </div>

      {view === "analytics" ? <RejectionAnalyticsPanel api={api}/> : (
      <>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] inline-flex items-center gap-1 pr-2">
          <Filter size={11}/> Filter
        </span>
        {[["submitted","New"],["under_review","In review"],["approved","Approved"],["rejected","Rejected"],["paid","Paid"],["all","All"]].map(([k,label]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`claim-filter-${k}`}
            className={`font-grotesk text-[10px] uppercase tracking-[0.22em] px-3 py-1.5 rounded-sm border ${
              filter === k ? "bg-[#D8B23E] text-black border-[#D8B23E]" : "border-white/15 text-[#8A8A82] hover:text-[#F5F5F0]"
            }`}>
            {label} <span className="opacity-70">· {counts[k] ?? 0}</span>
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="border border-white/10 rounded-sm bg-[#0a0a0a] p-8 text-center font-body text-sm text-[#8A8A82]">
          {loading ? "Loading…" : "No claims in this state right now."}
        </div>
      ) : (
        <div className="border border-white/10 rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-black/40">
              <tr>
                {["Order", "Buyer", "Material", "Order Value", "Claimed", "Status", "Submitted"].map(h => (
                  <th key={h} className="text-left font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82] px-3 py-2 border-b border-white/10">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => {
                const meta = STATUS_META[c.status] || { label: c.status, tone: "text-[#8A8A82] border-white/20" };
                return (
                  <tr key={c.id} data-testid={`claim-row-${c.id}`}
                      onClick={() => setActive(c)}
                      className="border-b border-white/5 hover:bg-white/[0.03] cursor-pointer">
                    <td className="px-3 py-3 font-mono text-[12px] text-[#F5F5F0]">{c.order_ref}</td>
                    <td className="px-3 py-3 font-body text-[12px] text-[#C9C9C2]">{c.buyer_email}</td>
                    <td className="px-3 py-3 font-body text-[12px] text-[#8A8A82]">{c.material}</td>
                    <td className="px-3 py-3 font-body text-[12px] text-[#F5F5F0]">{formatINR(c.order_value)}</td>
                    <td className="px-3 py-3 font-body text-[12px] text-[#D8B23E]">{formatINR(c.amount_claimed)}</td>
                    <td className="px-3 py-3">
                      <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 font-grotesk text-[9px] uppercase tracking-[0.18em] ${meta.tone}`}>
                        {c.status === "approved" || c.status === "paid" ? <ShieldCheck size={9}/> : <ShieldAlert size={9}/>}
                        {meta.label}
                      </span>
                    </td>
                    <td className="px-3 py-3 font-body text-[11px] text-[#8A8A82]">
                      {new Date(c.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <AnimatePresence>
        {active && <ClaimReviewDrawer api={api} claim={active}
                                       onClose={() => setActive(null)}
                                       onUpdated={() => { load(); setActive(null); }}/>}
      </AnimatePresence>
      </>
      )}
    </div>
  );
}


function RejectionAnalyticsPanel({ api }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/admin/claims/rejection-analytics");
        if (!cancel) setData(r.data);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Could not load analytics");
      } finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [api]);

  if (loading) return (
    <div data-testid="analytics-loading" className="border border-white/10 rounded-sm bg-[#0a0a0a] p-8 text-center font-body text-sm text-[#8A8A82]">
      <Loader2 size={16} className="inline animate-spin mr-2"/> Aggregating rejection signals…
    </div>
  );
  if (!data) return null;

  const maxReason = Math.max(1, ...data.top_reasons.map(r => r.count));
  const maxDoc = Math.max(1, ...data.doc_failure.map(d => d.hits));
  const maxCell = Math.max(1, ...data.heatmap.cells.flatMap(c => c.counts));
  const heatIntensity = (v) => {
    if (v === 0) return "rgba(255,255,255,0.03)";
    const pct = v / maxCell;
    return `rgba(229, 72, 77, ${0.15 + pct * 0.85})`;   // red gradient
  };

  return (
    <div data-testid="rejection-analytics-panel" className="space-y-6">
      {/* Totals strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          ["Total claims",       data.total_claims,     "#D8B23E"],
          ["Approved",           data.approved,         "#3DBB84"],
          ["Rejected",           data.rejected,         "#E5484D"],
          ["Rejection rate",     `${data.rejection_rate_pct}%`, "#B87333"],
        ].map(([k,v,c]) => (
          <div key={k} className="border border-white/10 rounded-sm bg-[#0a0a0a] p-4">
            <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82]">{k}</div>
            <div className="font-serif text-2xl mt-1" style={{ color: c }}>{v}</div>
          </div>
        ))}
      </div>

      {/* Top rejection reasons — bar */}
      <div className="border border-white/10 rounded-sm bg-[#0a0a0a] p-5">
        <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#D8B23E] mb-3">Top rejection reasons</div>
        {data.top_reasons.length === 0 ? (
          <div className="font-body text-sm text-[#8A8A82]">No rejections on record yet.</div>
        ) : (
          <div className="space-y-2" data-testid="top-reasons-list">
            {data.top_reasons.map((r, i) => (
              <div key={i} data-testid={`reason-row-${i}`} className="flex items-center gap-3">
                <div className="w-6 text-right font-mono text-[11px] text-[#8A8A82]">{r.count}</div>
                <div className="flex-1 h-6 bg-white/5 rounded-sm relative overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[#E5484D] to-[#B87333]"
                       style={{ width: `${(r.count / maxReason) * 100}%` }}/>
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 font-body text-[11px] text-[#F5F5F0] truncate max-w-[calc(100%-1rem)] pr-3">
                    {r.reason}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Doc-type failure hints */}
      <div className="border border-white/10 rounded-sm bg-[#0a0a0a] p-5">
        <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#D8B23E] mb-1">Doc-type failure signals</div>
        <div className="font-body text-[11px] text-[#8A8A82] mb-3">Which of the 5 mandatory uploads keeps triggering rejections.</div>
        <div className="grid sm:grid-cols-5 gap-2" data-testid="doc-failure-grid">
          {data.doc_failure.map((d) => (
            <div key={d.slot} data-testid={`doc-fail-${d.slot}`} className="border border-white/10 rounded-sm bg-black/30 p-3">
              <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82]">{d.label}</div>
              <div className="font-serif text-2xl text-[#F5F5F0] mt-1">{d.hits}</div>
              <div className="mt-2 h-1.5 bg-white/5 rounded-full overflow-hidden">
                <div className="h-full bg-[#E5484D]" style={{ width: `${(d.hits / maxDoc) * 100}%` }}/>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Monthly heatmap */}
      <div className="border border-white/10 rounded-sm bg-[#0a0a0a] p-5">
        <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#D8B23E] mb-1">Rejection heatmap · reasons × month</div>
        <div className="font-body text-[11px] text-[#8A8A82] mb-3">Trend view over the last 12 months. Darker red = more rejections that month for that reason.</div>
        {data.heatmap.reasons.length === 0 ? (
          <div className="font-body text-sm text-[#8A8A82]">Not enough data for a heatmap yet.</div>
        ) : (
          <div className="overflow-x-auto" data-testid="heatmap-table">
            <table className="w-full text-[10px] font-body border-separate" style={{ borderSpacing: 2 }}>
              <thead>
                <tr>
                  <th className="text-left text-[#8A8A82] font-grotesk uppercase tracking-[0.18em] pr-2 pb-2">Reason</th>
                  {data.heatmap.months.map(m => (
                    <th key={m} className="text-center text-[#8A8A82] font-mono pb-2 min-w-[54px]">{m.slice(5)}/{m.slice(2,4)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.heatmap.reasons.map((r, ri) => (
                  <tr key={r.key}>
                    <td className="text-left text-[#C9C9C2] pr-2 py-1 max-w-[280px] truncate" title={r.label}>{r.label}</td>
                    {data.heatmap.cells.map((cell, ci) => {
                      const v = cell.counts[ri] || 0;
                      return (
                        <td key={ci} data-testid={`heatmap-${ri}-${ci}`} className="text-center rounded-sm"
                            style={{ backgroundColor: heatIntensity(v), color: v === 0 ? "#4a4a44" : "#F5F5F0" }}>
                          {v}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}


function ClaimReviewDrawer({ api, claim, onClose, onUpdated }) {
  const [rejectReason, setRejectReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [docBlobs, setDocBlobs] = useState({});

  const openDoc = async (slot) => {
    try {
      const r = await api.get(`/claims/${claim.id}/document/${slot}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      setDocBlobs(prev => ({ ...prev, [slot]: url }));
      window.open(url, "_blank");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not fetch document"); }
  };

  const setStatus = async (status) => {
    if (status === "rejected" && !rejectReason.trim()) {
      toast.error("Please add a rejection reason first"); return;
    }
    setBusy(true);
    try {
      await api.patch(`/claims/${claim.id}/status`, {
        status,
        rejection_reason: status === "rejected" ? rejectReason.trim() : undefined,
      });
      toast.success(`Claim ${status.replace("_", " ")}`);
      onUpdated();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
    finally { setBusy(false); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur flex items-stretch justify-end"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <motion.div initial={{ x: 40, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 40, opacity: 0 }}
        className="bg-[#050506] w-full max-w-2xl border-l border-[#D8B23E]/30 h-full overflow-y-auto"
        data-testid="claim-review-drawer">
        <div className="sticky top-0 z-10 bg-[#050506]/95 backdrop-blur flex items-center justify-between border-b border-white/10 px-6 py-4">
          <div>
            <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#D8B23E]">Review Claim</div>
            <div className="font-serif text-xl text-[#F5F5F0] font-mono">{claim.order_ref}</div>
            <div className="font-body text-[11px] text-[#8A8A82]">{claim.buyer_email} · {claim.material}</div>
          </div>
          <button onClick={onClose} className="text-[#8A8A82] hover:text-[#F5F5F0]" data-testid="claim-drawer-close"><X size={20}/></button>
        </div>

        <div className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-3">
            {[
              ["Invoice #",   claim.invoice_number],
              ["Order value", formatINR(claim.order_value)],
              ["Amount paid", formatINR(claim.amount_paid)],
              ["Claimed",     formatINR(claim.amount_claimed)],
              ["Desk-computed",formatINR(claim.calculated_commission_inr)],
              ["Rate",        `${claim.rate_pct}%`],
            ].map(([k, v]) => (
              <div key={k} className="border border-white/10 rounded-sm bg-[#0a0a0a] p-3">
                <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82]">{k}</div>
                <div className="font-body text-sm text-[#F5F5F0] mt-1">{v}</div>
              </div>
            ))}
          </div>

          <div>
            <div className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] mb-2">Payout details</div>
            <div className="border border-white/10 rounded-sm bg-[#0a0a0a] p-3 font-mono text-[12px] text-[#C9C9C2]">
              {claim.payout_details}
            </div>
          </div>

          {claim.remarks && (
            <div>
              <div className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] mb-2">Remarks</div>
              <div className="border border-white/10 rounded-sm bg-[#0a0a0a] p-3 font-body text-[12px] text-[#C9C9C2]">
                {claim.remarks}
              </div>
            </div>
          )}

          <div>
            <div className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] mb-2">Documents · verify each</div>
            <div className="grid grid-cols-3 gap-2">
              {[["invoice", "Final Invoice"], ["lr", "Lorry Receipt"], ["unload", "Unloading Photo"]].map(([slot, label]) => (
                <button key={slot} onClick={() => openDoc(slot)}
                        data-testid={`claim-doc-${slot}`}
                        className="border border-white/15 hover:border-[#D8B23E] rounded-sm bg-black/30 p-3 text-left transition-colors">
                  <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#D8B23E] mb-1">{label}</div>
                  <div className="font-body text-[11px] text-[#C9C9C2] inline-flex items-center gap-1">
                    <Download size={11}/> Open document
                  </div>
                </button>
              ))}
            </div>
          </div>

          {claim.status === "rejected" && claim.rejection_reason && (
            <div className="border border-[#E5484D]/30 bg-[#E5484D]/[0.06] rounded-sm p-3">
              <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#E5484D]">Rejection reason</div>
              <div className="font-body text-[12px] text-[#F5F5F0] mt-1">{claim.rejection_reason}</div>
            </div>
          )}

          <div className="border-t border-white/10 pt-4 space-y-3">
            <div className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82]">Move claim</div>
            <div className="flex flex-wrap gap-2">
              {claim.status !== "under_review" && claim.status !== "approved" && claim.status !== "paid" && (
                <button onClick={() => setStatus("under_review")} disabled={busy}
                        data-testid="claim-move-review"
                        className="border border-[#2FE4C6]/40 text-[#2FE4C6] hover:bg-[#2FE4C6]/10 font-grotesk text-[10px] uppercase tracking-[0.22em] px-3 py-2 rounded-sm">Under review</button>
              )}
              {claim.status !== "approved" && claim.status !== "paid" && (
                <button onClick={() => setStatus("approved")} disabled={busy}
                        data-testid="claim-approve"
                        className="bg-[#3DBB84] hover:bg-[#2fa16e] disabled:opacity-40 text-black font-grotesk text-[10px] uppercase tracking-[0.22em] px-3 py-2 rounded-sm">Approve</button>
              )}
              {claim.status === "approved" && (
                <button onClick={() => setStatus("paid")} disabled={busy}
                        data-testid="claim-paid"
                        className="bg-[#D8B23E] hover:bg-[#c5a233] disabled:opacity-40 text-black font-grotesk text-[10px] uppercase tracking-[0.22em] px-3 py-2 rounded-sm">Mark paid</button>
              )}
            </div>
            <div>
              <div className="font-body text-[11px] text-[#8A8A82] mb-1">Reject with reason</div>
              <textarea data-testid="claim-reject-reason" rows={2} value={rejectReason}
                        onChange={e => setRejectReason(e.target.value)}
                        placeholder="e.g. LR not signed by consignee; invoice number does not match uploaded PDF."
                        className="w-full bg-black/40 border border-white/15 rounded-sm px-3 py-2 text-sm text-[#F5F5F0] focus:outline-none focus:border-[#E5484D]"/>
              <button onClick={() => setStatus("rejected")} disabled={busy || !rejectReason.trim()}
                      data-testid="claim-reject"
                      className="mt-2 border border-[#E5484D]/50 text-[#E5484D] hover:bg-[#E5484D]/10 disabled:opacity-40 font-grotesk text-[10px] uppercase tracking-[0.22em] px-3 py-2 rounded-sm">
                Reject with reason
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
